import { PRIORITIES, PRIORITY_META, type Priority, type TaskDTO } from '@taskos/shared';
import { AlarmClock, Archive, ArchiveRestore, Ban, Check, Flame, Pencil, Pin, RotateCcw } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type TouchEvent } from 'react';
import { useNavigate } from 'react-router';
import { Button, Menu } from '../../components/ui';
import { useUI } from '../../app/ui';
import { useTaskActions } from './actions';
import { PriorityBadge, TaskMeta } from './bits';

function stop(e: MouseEvent | KeyboardEvent) {
  e.stopPropagation();
}

export function isClosed(t: TaskDTO): boolean {
  return ['COMPLETED', 'CANCELLED', 'ARCHIVED'].includes(t.status);
}

interface QuickAction {
  key: string;
  label: string;
  icon: ReactNode;
  run: () => unknown;
  shortcut?: string;
  tone?: 'danger';
  /** Shown on phones too (the rest appear from tablet width up). */
  primary?: boolean;
}

/**
 * Every action a task allows, as visible buttons (no hidden "more" menu). Only permitted actions
 * are listed, so an open task shows at most six and a closed one at most two.
 */
export function useQuickActions(t: TaskDTO): QuickAction[] {
  const a = useTaskActions();
  const ui = useUI();
  if (isClosed(t))
    return [
      ...(t.can.reopen ? [{ key: 'reopen', label: 'Reopen', icon: <RotateCcw size={16} />, run: () => a.reopen(t), primary: true }] : []),
      ...(t.can.unarchive ? [{ key: 'unarchive', label: 'Unarchive', icon: <ArchiveRestore size={16} />, run: () => a.unarchive(t), primary: true }] : []),
      ...(t.can.archive ? [{ key: 'archive', label: 'Archive', icon: <Archive size={16} />, run: () => a.archive(t) }] : []),
    ];
  return [
    ...(t.can.snooze ? [{ key: 'snooze', label: 'Snooze', icon: <AlarmClock size={16} />, run: () => ui.openSnooze(t), shortcut: 'Z', primary: true }] : []),
    ...(t.can.edit ? [{ key: 'edit', label: 'Edit', icon: <Pencil size={16} />, run: () => ui.openTaskForm({ mode: 'edit', taskId: t.id }), shortcut: 'E', primary: true }] : []),
    { key: 'followup', label: 'Add follow-up', icon: <Pin size={16} />, run: () => ui.openFollowUp(t) },
    ...(t.can.cancel ? [{ key: 'cancel', label: 'Cancel task', icon: <Ban size={16} />, run: () => a.cancel(t), shortcut: 'X', tone: 'danger' as const }] : []),
    ...(t.can.archive ? [{ key: 'archive', label: 'Archive', icon: <Archive size={16} />, run: () => a.archive(t) }] : []),
  ];
}

/** Icon buttons for rows and tiles; every button has a tooltip and an accessible name. */
function QuickActionIcons({ t, actions, withPriority, priorityClass }: { t: TaskDTO; actions: QuickAction[]; withPriority?: boolean; priorityClass?: string }) {
  return (
    <>
      {actions.map((q) => (
        <Button
          key={q.key}
          variant="ghost"
          size="sm"
          iconOnly
          icon={q.icon}
          className={`qa ${q.tone === 'danger' ? 'qa-danger' : ''} ${q.primary ? '' : 'hide-mobile'}`}
          aria-label={`${q.label}: ${t.title}`}
          title={q.shortcut ? `${q.label} (${q.shortcut})` : q.label}
          onClick={() => q.run() as void}
          tabIndex={-1}
        />
      ))}
      {withPriority && t.can.edit && !isClosed(t) && <PriorityQuickMenu t={t} className={priorityClass} />}
    </>
  );
}

/** Four priority levels — a small picker is clearer than four extra buttons. */
function PriorityQuickMenu({ t, className }: { t: TaskDTO; className?: string }) {
  const a = useTaskActions();
  return (
    <span className={className} onClick={stop} onKeyDown={stop}>
      <Menu
        label="Change priority"
        items={PRIORITIES.map((p) => ({ label: `${PRIORITY_META[p].emoji} ${PRIORITY_META[p].label}`, disabled: t.priority === p, onSelect: () => a.setPriority(t, p), hint: t.priority === p ? '✓' : undefined }))}
        trigger={(p) => <Button variant="ghost" size="sm" iconOnly icon={<Flame size={16} />} className="qa" aria-label={`Change priority: ${t.title}`} title="Priority" tabIndex={-1} {...p} />}
      />
    </span>
  );
}

/** Inline priority chooser (task detail): all four levels visible, current one highlighted. */
export function PriorityChips({ t, onPick }: { t: TaskDTO; onPick: (p: Priority) => void }) {
  return (
    <div className="prio-chips" role="radiogroup" aria-label="Priority">
      {PRIORITIES.map((p) => (
        <button key={p} type="button" role="radio" aria-checked={t.priority === p} className={`prio-chip ${p}`} onClick={() => t.priority !== p && onPick(p)} title={PRIORITY_META[p].label}>
          <span className="prio-dot" aria-hidden />
          {PRIORITY_META[p].label}
        </button>
      ))}
    </div>
  );
}

/** Labelled buttons for the task page header (primary action first). */
export function TaskActionBar({ t }: { t: TaskDTO }) {
  const a = useTaskActions();
  const actions = useQuickActions(t);
  return (
    <div className="action-bar">
      {t.can.complete && (
        <Button variant="primary" icon={<Check size={16} />} onClick={() => a.complete(t)} title="Complete (C)">
          Complete
        </Button>
      )}
      {actions.map((q) => (
        <Button key={q.key} icon={q.icon} className={q.tone === 'danger' ? 'btn-danger-soft' : ''} onClick={() => q.run() as void} title={q.shortcut ? `${q.label} (${q.shortcut})` : undefined}>
          {q.key === 'followup' ? 'Follow-up' : q.key === 'cancel' ? 'Cancel' : q.label}
        </Button>
      ))}
    </div>
  );
}

export function TaskTile({ t, selected, onFocus, tabIndex = 0 }: { t: TaskDTO; selected?: boolean; onFocus?: () => void; tabIndex?: number }) {
  const nav = useNavigate();
  const ui = useUI();
  const a = useTaskActions();
  const actions = useQuickActions(t);
  const closed = isClosed(t);
  const lead = actions.filter((q) => q.tone !== 'danger' && q.key !== 'archive');
  const tail = actions.filter((q) => q.tone === 'danger' || q.key === 'archive');
  return (
    <article
      className={`tile ${t.priority} ${selected ? 'selected' : ''}`}
      tabIndex={tabIndex}
      data-task-id={t.id}
      aria-label={`${t.title}, ${PRIORITY_META[t.priority].label} priority`}
      onClick={() => nav(`/tasks/${t.id}`)}
      onFocus={() => {
        ui.setCurrent(t);
        onFocus?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) nav(`/tasks/${t.id}`);
      }}
    >
      <div className="flex between gap-sm">
        <PriorityBadge p={t.priority} />
        <span className="muted small mono">{t.ref}</span>
      </div>
      <div className={`tile-title ${closed ? 'done' : ''}`}>{t.title}</div>
      <TaskMeta t={t} />
      {t.nextAction && <div className="small muted truncate">→ {t.nextAction}</div>}
      <div className="tile-actions" onClick={stop} onKeyDown={stop}>
        {t.can.complete && <Button variant="ghost" size="sm" iconOnly icon={<Check size={16} />} className="qa qa-success" aria-label={`Complete: ${t.title}`} title="Complete (C)" onClick={() => a.complete(t)} tabIndex={-1} />}
        <QuickActionIcons t={t} actions={lead.map((q) => ({ ...q, primary: true }))} withPriority />
        <span className="spacer" />
        <QuickActionIcons t={t} actions={tail.map((q) => ({ ...q, primary: true }))} />
      </div>
    </article>
  );
}

const SWIPE_TRIGGER = 90;

/** List row: click opens, check completes, swipe right = complete, swipe left = snooze (touch). */
export function TaskRow({
  t,
  selected,
  selectable,
  checked,
  onToggleSelect,
  onFocus,
  tabIndex = 0,
}: {
  t: TaskDTO;
  selected?: boolean;
  selectable?: boolean;
  checked?: boolean;
  onToggleSelect?: () => void;
  onFocus?: () => void;
  tabIndex?: number;
}) {
  const nav = useNavigate();
  const ui = useUI();
  const a = useTaskActions();
  const actions = useQuickActions(t);
  const start = useRef<{ x: number; y: number } | null>(null);
  const [dx, setDx] = useState(0);
  const swiping = useRef(false);

  const onTouchStart = (e: TouchEvent) => {
    const p = e.touches[0]!;
    start.current = { x: p.clientX, y: p.clientY };
    swiping.current = false;
  };
  const onTouchMove = (e: TouchEvent) => {
    if (!start.current) return;
    const p = e.touches[0]!;
    const mx = p.clientX - start.current.x;
    const my = p.clientY - start.current.y;
    if (!swiping.current && Math.abs(mx) > 12 && Math.abs(mx) > Math.abs(my) * 1.5) swiping.current = true;
    if (swiping.current) setDx(Math.max(-140, Math.min(140, mx)));
  };
  const onTouchEnd = () => {
    const d = dx;
    start.current = null;
    setDx(0);
    if (!swiping.current) return;
    if (d > SWIPE_TRIGGER && t.can.complete) void a.complete(t);
    else if (d < -SWIPE_TRIGGER && t.can.snooze) ui.openSnooze(t);
  };

  const closed = isClosed(t);
  return (
    <div className="row-wrap">
      {dx !== 0 && (
        <div className="swipe-hint" style={{ background: dx > 0 ? 'var(--success)' : 'var(--warning)', justifyContent: dx > 0 ? 'flex-start' : 'flex-end' }} aria-hidden>
          {dx > 0 ? (dx > SWIPE_TRIGGER ? '✓ Release to complete' : '✓') : dx < -SWIPE_TRIGGER ? 'Release to snooze ⏰' : '⏰'}
        </div>
      )}
      <div
        className={`row ${selected ? 'selected' : ''}`}
        role="row"
        tabIndex={tabIndex}
        data-task-id={t.id}
        aria-selected={checked}
        style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: dx ? 'none' : 'transform .15s' }}
        onClick={() => {
          if (swiping.current) return;
          nav(`/tasks/${t.id}`);
        }}
        onFocus={() => {
          ui.setCurrent(t);
          onFocus?.();
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter') nav(`/tasks/${t.id}`);
          if (e.key === ' ' && selectable) {
            e.preventDefault();
            onToggleSelect?.();
          }
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex center gap-sm" onClick={stop}>
          {selectable && (
            <input type="checkbox" className="select-box" checked={!!checked} onChange={() => onToggleSelect?.()} aria-label={`Select ${t.title}`} tabIndex={-1} />
          )}
          <button
            type="button"
            className={`check ${closed && t.status === 'COMPLETED' ? 'done' : ''}`}
            aria-label={t.status === 'COMPLETED' ? 'Completed' : `Complete ${t.title}`}
            disabled={!t.can.complete}
            onClick={() => a.complete(t)}
            tabIndex={-1}
          >
            <Check size={14} />
          </button>
        </div>
        <div className="row-main">
          <div className="flex center gap-sm" style={{ minWidth: 0 }}>
            <PriorityBadge p={t.priority} short />
            <span className={`row-title ${closed ? 'done' : ''}`}>{t.title}</span>
          </div>
          <TaskMeta t={t} compact />
        </div>
        <div className="row-side" onClick={stop} onKeyDown={stop}>
          <span className="row-ref muted small mono hide-mobile">{t.ref}</span>
          <div className="row-actions">
            <QuickActionIcons t={t} actions={actions} withPriority priorityClass="hide-mobile" />
          </div>
        </div>
      </div>
    </div>
  );
}
